import {useEffect, useCallback, useRef, useState} from 'react';
import {useProductOptions, isBrowser, Head} from '@shopify/hydrogen';

import {ProductOptions} from '~/components';
import {useParamHelper} from '~/hooks/useParamHelper';

export function ProductOptionsForm({sanityProduct, currentCanonicalUrl}) {
  const sanityProductSlug = sanityProduct.store.slug.current;

  const {options, setSelectedOption, selectedOptions} = useProductOptions();
  const {scrubNonManualParams} = useParamHelper();
  const mounted = useRef(null);

  // Function to remove query parameters from URL for canonical link
  const cleanUrlForCanonical = (url) => {
    try {
      const urlObj = new URL(url);
      // Return the URL without any search parameters for the canonical link
      return urlObj.origin + urlObj.pathname;
    } catch (e) {
      console.error('Error cleaning URL for canonical link:', e);
      // Return the original URL if there's an issue parsing
      return url;
    }
  };

  useEffect(() => {
    let newUrl = `${sanityProductSlug}`;

    let optionHasMultipleValues = false;

    for (const option of options) {
      if (option.values.length > 1) {
        optionHasMultipleValues = true;
        break;
      }
    }

    if (optionHasMultipleValues) {
      for (const [key, value] of Object.entries(selectedOptions)) {
        const formattedValue = value
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/\//g, '-');
        newUrl += `-${formattedValue}`;
      }
    }

    if (isBrowser()) {
      // Maintain the full URL including query parameters in the browser
      const originalQueryString = window.location.search;
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${originalQueryString}`,
      );
    }

    mounted.current++;
  }, [options, selectedOptions, sanityProductSlug]);

  const handleChange = useCallback(
    (name, value) => {
      setSelectedOption(name, value);
    },
    [setSelectedOption],
  );

  return (
    <>
      <Head>
        {/* Set the canonical URL without query parameters */}
        <link
          rel="canonical"
          href={cleanUrlForCanonical(currentCanonicalUrl)}
        />
      </Head>
      <form className="product-detail__options-form">
        {options.map(({name, values}) => {
          if (values.length === 1) {
            return null;
          }
          return (
            <div key={name}>
              <h5>{`${name}: ${selectedOptions[name]}`}</h5>
              <div className="product-options">
                <ProductOptions
                  name={name}
                  handleChange={handleChange}
                  values={values}
                />
              </div>
            </div>
          );
        })}
      </form>
    </>
  );
}
